import { Skel } from "../components/Skel";
import { SkelText } from "../components/skeleton/SkelText";

// Equipo's loading silhouettes (norma de skeletons, ADR-0166). Each one
// reuses the real containers of what replaces it — the roster's `.panel`
// + `.dtable` with its static headers, the providers' table, the invite
// form's `.panel__body` and the `.store-picker` fieldset box — so the data
// lands without a layout shift. Row counts fill the 1280×800 viewport
// above the invite form.

const ROSTER_HEADERS = ["Miembro", "Rol", "Acceso", "Última actividad", "Estado"] as const;
const PROVIDER_HEADERS = [
  { label: "Proveedor" },
  { label: "Rubro" },
  { label: "SLA contrato", num: true },
  { label: "Cierre promedio", num: true },
  { label: "Cumplimiento" },
  { label: "Activas", num: true },
] as const;
const WIDTHS = ["60%", "45%", "70%", "40%", "55%"] as const;

/** The avatar + name/email pair of a `.user-cell`. */
function SkelUserCell() {
  return (
    <div className="user-cell">
      <Skel variant="circle" width={28} />
      <div className="skel-stack">
        <Skel width={128} height={12} />
        <Skel width={168} height={9} />
      </div>
    </div>
  );
}

function SkelRows({ rows, cols }: { rows: number; cols: number }) {
  return (
    <tbody aria-hidden="true">
      {Array.from({ length: rows }, (_, r) => (
        <tr key={r}>
          {Array.from({ length: cols }, (_, c) => (
            <td key={c}>
              {c === 0 ? (
                <SkelUserCell />
              ) : c === cols - 1 && cols === ROSTER_HEADERS.length ? (
                <Skel width={64} height={20} pill />
              ) : (
                <Skel width={WIDTHS[(r + c) % WIDTHS.length] ?? "50%"} height={12} />
              )}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

/** The «Proveedores externos» table, for the section's own boundary. */
export function ProveedoresTableSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <table className="dtable">
      <thead>
        <tr>
          {PROVIDER_HEADERS.map((h) => (
            <th key={h.label} className={"num" in h ? "col-num" : undefined}>
              {h.label}
            </th>
          ))}
        </tr>
      </thead>
      <SkelRows rows={rows} cols={PROVIDER_HEADERS.length} />
    </table>
  );
}

/** The page body while the roster (and the recipe it is filtered by)
 *  loads: the members panel, the invite form when the caller can invite,
 *  and the providers panel when the vertical has it. */
export function EquipoSkeleton({
  showInvite,
  showProveedores,
}: {
  showInvite: boolean;
  showProveedores: boolean;
}) {
  return (
    <div className="stack-gap">
      <div className="panel">
        <div className="panel__head">
          <div className="panel__title">Miembros internos</div>
          <div className="panel__sub mono">
            <Skel variant="text" width={72} />
          </div>
        </div>
        <div className="panel__body--flush">
          <table className="dtable">
            <thead>
              <tr>
                {ROSTER_HEADERS.map((h) => (
                  <th key={h} scope="col">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <SkelRows rows={6} cols={ROSTER_HEADERS.length} />
          </table>
        </div>
        {showInvite && (
          <div className="panel__body" aria-hidden="true">
            <div className="skel-stack">
              <Skel variant="text" width={110} />
              <div className="skel-row">
                <Skel width="100%" height={34} />
                <Skel width={160} height={34} />
                <Skel variant="btn" width={72} />
              </div>
              <SkelText lines={2} />
            </div>
          </div>
        )}
      </div>
      {showProveedores && (
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Proveedores externos</div>
            <div className="panel__sub mono">
              <Skel variant="text" width={220} />
            </div>
          </div>
          <div className="panel__body--flush">
            <ProveedoresTableSkeleton />
          </div>
        </div>
      )}
    </div>
  );
}

/** The `.store-picker` fieldset box while the assignable stores load. */
export function StorePickerSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="store-picker" aria-hidden="true">
      <div className="store-picker__legend">
        <Skel variant="text" width="40%" />
      </div>
      <div className="store-picker__list">
        {Array.from({ length: rows }, (_, i) => (
          <div key={i} className="store-picker__option">
            <Skel width={14} height={14} />
            <Skel variant="text" width={WIDTHS[i % WIDTHS.length] ?? "50%"} />
          </div>
        ))}
      </div>
    </div>
  );
}
